import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError, absoluteUrl } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useTrackerAccess } from "@/context/TrackerAccessContext";
import { useWorkspaceBrand } from "@/context/WorkspaceBrandContext";
import { DatePicker } from "@/components/DatePicker";
import { useConfirm } from "@/context/ConfirmContext";
import { TrackerImg } from "@/components/TrackerPhoto";
import { SortableList } from "@/components/SortableList";
import { toast } from "sonner";
import {
  Buildings, Plus, MapPin, Calendar, X, Trash, ShieldCheck, ListChecks, ClockCounterClockwise,
  PauseCircle, CheckCircle, PlayCircle, ClockClockwise,
} from "@phosphor-icons/react";

const STATUS_STYLE = {
  "Not Started": "bg-neutral-100 text-neutral-600",
  "In Progress": "bg-blue-50 text-blue-700",
  "On Hold": "bg-amber-50 text-amber-700",
  "Completed": "bg-emerald-50 text-emerald-700",
};

export default function Tracker() {
  const { user } = useAuth();
  const trackerAccess = useTrackerAccess();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "projects" ? "projects" : "dashboard"); // dashboard | projects | reports | admin

  if (trackerAccess?.loading) return <div className="p-8 text-neutral-500">Loading…</div>;
  if (!trackerAccess?.hasAccess) {
    return (
      <div className="p-6 md:p-8 max-w-2xl mx-auto text-center mt-16" data-testid="tracker-no-access">
        <ShieldCheck size={40} className="mx-auto text-neutral-300 mb-3" />
        <h1 className="font-display text-2xl font-bold text-neutral-900">No access yet</h1>
        <p className="text-sm text-neutral-500 mt-2">Ask an admin to grant you access to Projects by Gray Owl.</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="tracker-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">Site Tracking</div>
          <h1 className="font-display text-3xl font-bold text-neutral-900 mt-1">Projects by Gray Owl</h1>
        </div>
      </div>
      <div className="border-b border-neutral-200 flex gap-6 mb-6">
        <button data-testid="tracker-tab-dashboard" onClick={() => setTab("dashboard")} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${tab === "dashboard" ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>Dashboard</button>
        <button data-testid="tracker-tab-projects" onClick={() => setTab("projects")} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${tab === "projects" ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>Projects</button>
        <button data-testid="tracker-tab-reports" onClick={() => setTab("reports")} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${tab === "reports" ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>Site Reports</button>
        {user?.role === "admin" && (
          <button data-testid="tracker-tab-admin" onClick={() => setTab("admin")} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${tab === "admin" ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>Access &amp; Settings</button>
        )}
      </div>
      {tab === "dashboard" && <DashboardTab />}
      {tab === "projects" && <ProjectsTab isAdmin={user?.role === "admin"} />}
      {tab === "reports" && <ReportsTab trackerProjects={trackerAccess.projects} />}
      {tab === "admin" && user?.role === "admin" && <AdminTab />}
    </div>
  );
}

function ProjectCard({ p }) {
  const nav = useNavigate();
  return (
    <div data-testid={`tracker-project-card-${p.id}`} onClick={() => nav(`/tracker/projects/${p.id}`)} className={`dos-card overflow-hidden cursor-pointer hover:shadow-md transition-shadow duration-200 group ${p.is_archived ? "opacity-70" : ""}`}>
      <div className="h-32 bg-neutral-100 relative overflow-hidden">
        <TrackerImg photoId={p.latest_photo_id} className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300" alt={p.name} />
        <span data-testid={`tracker-project-card-badge-${p.id}`} className={`absolute top-2 right-2 dos-badge ${p.is_archived ? "bg-neutral-200 text-neutral-500" : (STATUS_STYLE[p.status] || "bg-neutral-100 text-neutral-600")}`}>{p.is_archived ? "Archived" : p.status}</span>
      </div>
      <div className="p-4">
        <div className="font-display font-semibold text-neutral-900 truncate">{p.name}</div>
        <div className="text-xs text-neutral-500 mt-0.5 truncate">{p.client_name || "No client linked"}</div>
        {p.location && <div className="text-xs text-neutral-400 flex items-center gap-1 mt-1 truncate"><MapPin size={11} /> {p.location}</div>}
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-neutral-500">Overall</span>
            <span className="font-mono font-semibold text-neutral-900">{p.overall_percent}%</span>
          </div>
          <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
            <div className="h-full bg-neutral-900 rounded-full transition-all duration-500" style={{ width: `${p.overall_percent}%` }} />
          </div>
          {p.towers?.length > 0 && (
            <div className="mt-2 space-y-1" data-testid={`project-card-towers-${p.id}`}>
              {p.towers.map((t) => (
                <div key={t.id} className="flex items-center gap-2 text-[11px]">
                  <span className="w-20 truncate text-neutral-500">{t.name}</span>
                  <div className="flex-1 h-1 bg-neutral-100 rounded-full overflow-hidden"><div className="h-full bg-neutral-500 rounded-full" style={{ width: `${t.percent}%` }} /></div>
                  <span className="font-mono text-neutral-700 w-9 text-right">{t.percent}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="text-[11px] text-neutral-400 mt-2">Last updated: {p.last_activity_update ? new Date(p.last_activity_update).toLocaleDateString() : "Never"}</div>
      </div>
    </div>
  );
}

function DashboardTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/tracker/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-neutral-500">Loading…</div>;
  return (
    <div data-testid="tracker-dashboard-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SummaryStat label="Active Projects" value={data.summary.active} icon={PlayCircle} tone="blue" testId="tracker-stat-active" />
        <SummaryStat label="Completed" value={data.summary.completed} icon={CheckCircle} tone="green" testId="tracker-stat-completed" />
        <SummaryStat label="On Hold" value={data.summary.on_hold} icon={PauseCircle} tone="amber" testId="tracker-stat-onhold" />
        <SummaryStat label="Needs Update" value={data.summary.needs_update} icon={ClockClockwise} tone="red" testId="tracker-stat-needsupdate" />
      </div>
      {data.projects.length === 0 ? (
        <div className="dos-card p-12 text-center text-neutral-500 text-sm">No projects yet. Ask an admin to create one.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.projects.map((p) => <ProjectCard key={p.id} p={p} />)}
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, icon: Icon, tone, testId }) {
  const toneCls = { blue: "text-blue-600 bg-blue-50", green: "text-emerald-600 bg-emerald-50", amber: "text-amber-600 bg-amber-50", red: "text-red-600 bg-red-50" }[tone];
  return (
    <div className="dos-card p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${toneCls}`}><Icon size={14} weight="bold" /></div>
      </div>
      <div className="font-display text-2xl font-bold text-neutral-900 mt-2">{value}</div>
    </div>
  );
}

function ProjectsTab({ isAdmin }) {
  const [projects, setProjects] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const load = async () => {
    const params = statusFilter === "Archived" ? "?archived=true" : (statusFilter !== "all" ? `?status=${encodeURIComponent(statusFilter)}` : "");
    const { data } = await api.get(`/tracker/projects${params}`);
    setProjects(data);
  };
  useEffect(() => { load(); }, [statusFilter]);

  const filters = ["all", "Not Started", "In Progress", "On Hold", "Completed", ...(isAdmin ? ["Archived"] : [])];

  return (
    <div data-testid="tracker-projects-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {filters.map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`tracker-filter-${s.replace(/ /g, "-")}`} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${statusFilter === s ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>{s === "all" ? "All" : s}</button>
        ))}
        {isAdmin && (
          <button data-testid="new-tracker-project-btn" onClick={() => setOpen(true)} className="dos-btn dos-btn-primary ml-auto"><Plus size={14} /> New Project</button>
        )}
      </div>
      {projects.length === 0 ? (
        <div className="dos-card p-12 text-center text-neutral-500 text-sm">{statusFilter === "Archived" ? "No archived projects." : "No projects in this view."}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => <ProjectCard key={p.id} p={p} />)}
        </div>
      )}
      {open && <NewProjectModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function NewProjectModal({ onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ name: "", client_id: "", location: "", start_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tracker/projects", form);
      toast.success("Project created");
      onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/30 overflow-y-auto p-4 flex items-start justify-center" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-md my-8 fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <div className="font-display font-semibold text-neutral-900">New Project</div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <input data-testid="tracker-new-name" required placeholder="Project name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="dos-input" />
          <select data-testid="tracker-new-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="dos-input">
            <option value="">— No client linked —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="dos-input" />
          <div><label className="text-xs text-neutral-500 mb-1 block">Start Date</label><DatePicker testId="tracker-new-start-date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} /></div>
          <p className="text-[11px] text-neutral-400">A standard 19-step activity checklist will be added automatically — you can customise it afterwards.</p>
        </div>
        <div className="px-5 py-4 border-t border-neutral-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
          <button data-testid="tracker-new-save" type="submit" disabled={saving} className="dos-btn dos-btn-primary">{saving ? "Creating…" : "Create Project"}</button>
        </div>
      </form>
    </div>
  );
}

function ReportsTab({ trackerProjects }) {
  const [reports, setReports] = useState([]);
  const [projectId, setProjectId] = useState("all");
  const [status, setStatus] = useState("all");
  const nav = useNavigate();

  const load = async () => {
    const params = new URLSearchParams();
    if (projectId !== "all") params.set("project_id", projectId);
    if (status !== "all") params.set("status", status);
    const { data } = await api.get(`/tracker/reports?${params.toString()}`);
    setReports(data);
  };
  useEffect(() => { load(); }, [projectId, status]);

  return (
    <div data-testid="tracker-reports-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select data-testid="reports-filter-project" value={projectId} onChange={(e) => setProjectId(e.target.value)} className="dos-input" style={{ maxWidth: 220 }}>
          <option value="all">All Projects</option>
          {trackerProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="dos-input" style={{ maxWidth: 180 }}>
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="pending_review">Pending Review</option>
          <option value="finalized">Finalised</option>
        </select>
      </div>
      <div className="dos-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
              <th className="px-4 py-3">Project</th><th className="px-4 py-3">Visit Date</th><th className="px-4 py-3">Generated By</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Last Updated</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50 cursor-pointer" data-testid={`tracker-report-row-${r.id}`} onClick={() => nav(`/tracker/projects/${r.project_id}?report=${r.id}`)}>
                <td className="px-4 py-3 font-medium text-neutral-900">{r.project_name}{r.tower_name && <span className="text-xs font-normal text-neutral-500"> · {r.tower_name}</span>}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-500">{r.visit_date}</td>
                <td className="px-4 py-3 text-neutral-600">{r.generated_by_name}</td>
                <td className="px-4 py-3"><span className={`dos-badge ${r.status === "finalized" ? "bg-emerald-50 text-emerald-700" : r.status === "pending_review" ? "bg-amber-50 text-amber-700" : "bg-neutral-100 text-neutral-600"}`}>{r.status === "pending_review" ? "Pending Review" : r.status === "finalized" ? "Finalised" : "Draft"}</span></td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-500">{new Date(r.updated_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right text-neutral-400"><ListChecks size={14} /></td>
              </tr>
            ))}
            {reports.length === 0 && <tr><td colSpan={6} className="p-12 text-center text-neutral-500 text-sm">No site visit reports yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminTab() {
  const [sub, setSub] = useState("access"); // access | template | branding | audit
  return (
    <div data-testid="tracker-admin-tab">
      <div className="flex gap-2 mb-5">
        {[["access", "Access"], ["template", "Activity Template"], ["branding", "Report Branding"], ["audit", "Audit Log"]].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} className={`px-3 py-1.5 text-sm rounded-md transition-colors ${sub === k ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100"}`} data-testid={`tracker-admin-sub-${k}`}>{l}</button>
        ))}
      </div>
      {sub === "access" && <AccessAdmin />}
      {sub === "template" && <TemplateAdmin />}
      {sub === "branding" && <BrandingAdmin />}
      {sub === "audit" && <AuditAdmin />}
    </div>
  );
}

function BrandingAdmin() {
  const { workspace, openBrandEditor } = useWorkspaceBrand();
  const logoSrc = workspace.logo_url ? absoluteUrl(workspace.logo_url) : "";
  return (
    <div className="dos-card p-5 max-w-lg" data-testid="tracker-branding-admin">
      <p className="text-xs text-neutral-500 mb-4">This logo, company name and tagline appear on newly generated Site Visit Report PDFs (and the login screen). It's the same branding used across your whole workspace.</p>
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-md border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden shrink-0">
          {logoSrc ? <img src={logoSrc} alt="Company logo" data-testid="branding-logo-preview" className="w-full h-full object-contain" /> : <Buildings size={22} className="text-neutral-300" />}
        </div>
        <div className="min-w-0">
          <div className="font-display font-semibold text-neutral-900 truncate" data-testid="branding-name-preview">{workspace.name || "Gray Owl"}</div>
          <div className="text-xs text-neutral-500 uppercase tracking-wider truncate" data-testid="branding-tagline-preview">{workspace.tagline || "Workspace"}</div>
        </div>
      </div>
      <button type="button" data-testid="branding-edit-btn" onClick={openBrandEditor} className="dos-btn dos-btn-primary mt-4">Edit Branding</button>
    </div>
  );
}

function AccessAdmin() {
  const [perms, setPerms] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ user_id: "", project_id: "", role: "viewer" });
  const confirm = useConfirm();

  const load = async () => {
    const [p, u, pr] = await Promise.all([api.get("/tracker/admin/permissions"), api.get("/users"), api.get("/tracker/projects")]);
    setPerms(p.data); setUsers(u.data.filter((x) => x.role !== "admin")); setProjects(pr.data);
  };
  useEffect(() => { load(); }, []);

  const grant = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.project_id) { toast.error("Pick a user and a project"); return; }
    try { await api.post("/tracker/admin/permissions", form); toast.success("Access granted"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const revoke = async (perm) => {
    const ok = await confirm({ title: "Revoke access?", message: `${perm.user_name} will immediately lose access to "${perm.project_name}".`, confirmLabel: "Revoke", tone: "danger" });
    if (!ok) return;
    try { await api.delete(`/tracker/admin/permissions/${perm.id}`); toast.success("Access revoked"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div data-testid="tracker-access-admin">
      <form onSubmit={grant} className="dos-card p-4 mb-5 flex flex-wrap items-end gap-2">
        <div>
          <label className="text-xs text-neutral-500 mb-1 block">User</label>
          <select data-testid="grant-user-select" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className="dos-input" style={{ minWidth: 180 }}>
            <option value="">Select user…</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-neutral-500 mb-1 block">Project</label>
          <select data-testid="grant-project-select" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="dos-input" style={{ minWidth: 180 }}>
            <option value="">Select project…</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-neutral-500 mb-1 block">Role</label>
          <select data-testid="grant-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="dos-input">
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
          </select>
        </div>
        <button data-testid="grant-access-btn" type="submit" className="dos-btn dos-btn-primary"><Plus size={14} /> Grant Access</button>
      </form>
      <div className="dos-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200"><th className="px-4 py-3">User</th><th className="px-4 py-3">Project</th><th className="px-4 py-3">Role</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {perms.map((p) => (
              <tr key={p.id} className="border-b border-neutral-100 last:border-0" data-testid={`perm-row-${p.id}`}>
                <td className="px-4 py-3">{p.user_name}<div className="text-xs text-neutral-400">{p.user_email}</div></td>
                <td className="px-4 py-3">{p.project_name}</td>
                <td className="px-4 py-3"><span className="dos-badge bg-neutral-100 text-neutral-700 capitalize">{p.role}</span></td>
                <td className="px-4 py-3 text-right"><button onClick={() => revoke(p)} data-testid={`revoke-${p.id}`} className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash size={14} /></button></td>
              </tr>
            ))}
            {perms.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-neutral-500 text-sm">No one has been granted access yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TemplateAdmin() {
  const [activities, setActivities] = useState([]);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const keySeq = useRef(0);
  const wrap = (name) => ({ key: `t${keySeq.current++}`, name });
  useEffect(() => { api.get("/tracker/admin/activity-template").then((r) => setActivities(r.data.activities.map(wrap))); }, []); // eslint-disable-line

  const remove = (idx) => setActivities(activities.filter((_, i) => i !== idx));
  const add = () => { if (!newName.trim()) return; setActivities([...activities, wrap(newName.trim())]); setNewName(""); };
  const save = async () => {
    setSaving(true);
    try { await api.put("/tracker/admin/activity-template", { activities: activities.map((a) => a.name) }); toast.success("Template saved — applies to future new projects"); }
    catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="dos-card p-5 max-w-xl" data-testid="tracker-template-admin">
      <p className="text-xs text-neutral-500 mb-3">This is the default checklist applied to every <b>new</b> project. Existing projects are unaffected.</p>
      <SortableList className="space-y-1.5 mb-3" items={activities} getId={(a) => a.key} onReorder={setActivities} renderItem={(a, idx, handle) => (
          <div className="flex items-center gap-2 bg-neutral-50 rounded-md px-2 py-1.5" data-testid={`template-row-${idx}`}>
            {handle}
            <span className="text-sm flex-1 truncate" data-testid={`template-row-name-${idx}`}>{a.name}</span>
            <button onClick={() => remove(idx)} data-testid={`template-remove-${idx}`} className="p-1 rounded hover:bg-red-100 text-neutral-400 hover:text-red-500"><Trash size={12} /></button>
          </div>
        )} />
      {activities.length > 0 && <p className="text-[11px] text-neutral-400 -mt-1 mb-3">Drag the handle to reorder, then save.</p>}
      <div className="flex gap-2 mb-4">
        <input data-testid="template-new-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New activity name" className="dos-input flex-1" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} />
        <button type="button" onClick={add} className="dos-btn dos-btn-secondary"><Plus size={14} /></button>
      </div>
      <button data-testid="template-save-btn" onClick={save} disabled={saving} className="dos-btn dos-btn-primary">{saving ? "Saving…" : "Save Template"}</button>
    </div>
  );
}

function AuditAdmin() {
  const [entries, setEntries] = useState([]);
  useEffect(() => { api.get("/tracker/admin/audit").then((r) => setEntries(r.data)); }, []);
  return (
    <div className="dos-card overflow-hidden" data-testid="tracker-audit-admin">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200"><th className="px-4 py-3">When</th><th className="px-4 py-3">Who</th><th className="px-4 py-3">Project</th><th className="px-4 py-3">What</th></tr></thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id} className="border-b border-neutral-100 last:border-0">
              <td className="px-4 py-3 font-mono text-xs text-neutral-500">{new Date(e.created_at).toLocaleString()}</td>
              <td className="px-4 py-3">{e.user_name}</td>
              <td className="px-4 py-3">{e.project_name}</td>
              <td className="px-4 py-3 text-neutral-600">{e.detail}</td>
            </tr>
          ))}
          {entries.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-neutral-500 text-sm">No activity recorded yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
